'use client';

import { useEffect, useState } from 'react';

export type ScriptStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Module-level cache so a given provider script is injected at most once per
 * page, regardless of how many embeds of that provider are rendered, and its
 * load state is shared across instances and App Router client navigations.
 */
const scriptStatusCache = new Map<string, ScriptStatus>();

/**
 * Injects a third-party `<script>` once (deduped by `src`) and reports its load
 * status. Mirrors the in-house script-injection pattern used by OneTrustNotice.
 * Safe across client-side navigation: an already-loaded script resolves to
 * `ready` immediately instead of being re-injected.
 */
export function useExternalScript(src?: string): ScriptStatus {
  const [status, setStatus] = useState<ScriptStatus>(() =>
    src ? (scriptStatusCache.get(src) ?? 'idle') : 'idle',
  );

  useEffect(() => {
    if (!src) return;

    let script = document.querySelector<HTMLScriptElement>(
      `script[data-embed-src="${src}"]`,
    );

    // The DOM element's persisted status is the source of truth: it survives a
    // module-cache reset (HMR). Hydrate the cache from it so we never get stuck
    // in 'loading' for a script whose load event already fired.
    const domStatus = script?.dataset.embedStatus as ScriptStatus | undefined;
    if (script && domStatus) {
      scriptStatusCache.set(src, domStatus);
    } else if (scriptStatusCache.get(src) === 'ready' && !script) {
      // Cached ready but the tag is gone (tests/manual removal) — re-inject.
      scriptStatusCache.delete(src);
    }

    if (scriptStatusCache.get(src) === 'ready' && script) {
      setStatus('ready');
      return;
    }

    const handleLoad = () => {
      scriptStatusCache.set(src, 'ready');
      if (script) script.dataset.embedStatus = 'ready';
      setStatus('ready');
    };
    const handleError = () => {
      scriptStatusCache.set(src, 'error');
      if (script) script.dataset.embedStatus = 'error';
      setStatus('error');
    };

    if (!script) {
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.embedSrc = src;
      script.dataset.embedStatus = 'loading';
      scriptStatusCache.set(src, 'loading');
      setStatus('loading');
      // Attach listeners BEFORE insertion so a fast (cached) load event can't
      // fire before we're listening and leave us stuck in 'loading'.
      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
      document.body.appendChild(script);
    } else {
      setStatus(scriptStatusCache.get(src) ?? 'loading');
      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
    }

    return () => {
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
    };
  }, [src]);

  return status;
}
